import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { PageHeader, SectionCard, Loading, EmptyState } from "@/components/common/ui";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import {
  Receipt, PiggyBank, Target, Megaphone, History, Plus, Pencil, Trash2,
  ShieldCheck, SlidersHorizontal, MessageSquare, Search, ArrowRight, CalendarDays,
} from "lucide-react";

const ENTITY_META = {
  Spend: { icon: Receipt, color: "hsl(var(--chart-cat-2))" },
  Budget: { icon: PiggyBank, color: "hsl(var(--chart-cat-7))" },
  BudgetPlan: { icon: PiggyBank, color: "hsl(var(--chart-cat-7))" },
  BudgetEfficiency: { icon: SlidersHorizontal, color: "hsl(var(--chart-cat-5))" },
  Target: { icon: Target, color: "hsl(var(--chart-cat-4))" },
  MarketingTarget: { icon: Target, color: "hsl(var(--chart-cat-4))" },
  BenchmarkTarget: { icon: ShieldCheck, color: "hsl(var(--info))" },
  Campaign: { icon: Megaphone, color: "hsl(var(--chart-cat-6))" },
};
const ACTION_META = {
  create: { icon: Plus, chip: "bg-[hsl(var(--success)/0.14)] text-[hsl(var(--success))]", label: "Created" },
  update: { icon: Pencil, chip: "bg-[hsl(var(--info)/0.14)] text-[hsl(var(--info))]", label: "Updated" },
  delete: { icon: Trash2, chip: "bg-[hsl(var(--danger)/0.14)] text-[hsl(var(--danger))]", label: "Deleted" },
};
const ENTITIES = ["all", "BenchmarkTarget", "BudgetPlan", "BudgetEfficiency", "MarketingTarget", "Spend", "Budget", "Campaign"];

function fmtVal(v, money) {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "number" && Math.abs(v) >= 1000) return money(v);
  return String(v);
}

function timeOf(iso) {
  try {
    return new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  } catch (e) { return ""; }
}

function dayKey(iso) {
  try {
    const d = new Date(iso);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  } catch (e) { return "unknown"; }
}

function dayLabel(key) {
  const today = dayKey(new Date().toISOString());
  const yest = dayKey(new Date(Date.now() - 86400000).toISOString());
  if (key === today) return "Today";
  if (key === yest) return "Yesterday";
  try {
    return new Date(`${key}T00:00:00`).toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
  } catch (e) { return key; }
}

export default function AuditLog() {
  const { money } = useCurrency();
  const [entity, setEntity] = useState("all");
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = entity === "all" ? { limit: 300 } : { entity, limit: 300 };
    api.audit(params).then((d) => { setRows(d); setLoading(false); }).catch(() => setLoading(false));
  }, [entity]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const blob = [
        r.entity_type, r.label, r.changed_by, r.reason, r.action,
        ...(r.changes || []).flatMap((c) => [c.field, c.old, c.new]),
      ].map((x) => String(x ?? "").toLowerCase()).join(" ");
      return blob.includes(q);
    });
  }, [rows, query]);

  const stats = useMemo(() => {
    const s = { total: filtered.length, create: 0, update: 0, delete: 0 };
    filtered.forEach((r) => { s[r.action] = (s[r.action] || 0) + 1; });
    return s;
  }, [filtered]);

  const groups = useMemo(() => {
    const g = [];
    let cur = null;
    filtered.forEach((r) => {
      const k = dayKey(r.changed_at);
      if (!cur || cur.key !== k) { cur = { key: k, items: [] }; g.push(cur); }
      cur.items.push(r);
    });
    return g;
  }, [filtered]);

  return (
    <div className="space-y-5" data-testid="audit-log-page">
      <PageHeader title="Audit Log" description="Full version history of every governed change — benchmark targets, budget plans, efficiency rates, spends & campaigns — with who changed what, why, and when.">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search change, person, field…"
              className="h-9 w-[230px] pl-8" data-testid="audit-search" />
          </div>
          <Select value={entity} onValueChange={setEntity}>
            <SelectTrigger className="h-9 w-[190px]" data-testid="audit-entity-filter"><SelectValue /></SelectTrigger>
            <SelectContent>{ENTITIES.map((e) => <SelectItem key={e} value={e} data-testid={`audit-entity-${e}`}>{e === "all" ? "All changes" : e}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </PageHeader>

      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        {[
          { k: "total", label: "Total Changes", color: "hsl(var(--chart-cat-7))" },
          { k: "create", label: "Created", color: "hsl(var(--success))" },
          { k: "update", label: "Updated", color: "hsl(var(--info))" },
          { k: "delete", label: "Deleted", color: "hsl(var(--danger))" },
        ].map((s) => (
          <div key={s.k} className="rounded-xl border bg-card p-4 card-shadow" data-testid={`audit-stat-${s.k}`}>
            <p className="text-xs font-medium text-muted-foreground">{s.label}</p>
            <p className="mt-1 font-heading text-2xl font-semibold tabular-nums" style={{ color: s.color }}>{stats[s.k] || 0}</p>
          </div>
        ))}
      </div>

      <SectionCard title="Change History" description={`${filtered.length} entr${filtered.length === 1 ? "y" : "ies"}${query ? ` matching “${query}”` : ""}`} testId="card-audit-timeline">
        {loading ? <div className="p-4"><Loading rows={5} /></div> : filtered.length === 0 ? (
          <div className="p-6"><EmptyState title={query ? "No matches" : "No changes yet"} message={query ? "Try a different search term or clear the filter." : "As soon as someone edits a spend, budget, target or campaign, it will appear here."} /></div>
        ) : (
          <div>
            {groups.map((g) => (
              <div key={g.key} data-testid={`audit-day-group-${g.key}`}>
                <div className="sticky top-0 z-10 flex items-center gap-2 border-y bg-[hsl(var(--surface-2))] px-4 py-1.5 backdrop-blur first:border-t-0" data-testid="audit-day-header">
                  <CalendarDays className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{dayLabel(g.key)}</span>
                  <span className="text-[11px] text-muted-foreground">· {g.items.length} change{g.items.length === 1 ? "" : "s"}</span>
                </div>
                <div className="divide-y">
                  {g.items.map((r) => {
                    const em = ENTITY_META[r.entity_type] || { icon: History, color: "hsl(var(--muted-foreground))" };
                    const am = ACTION_META[r.action] || ACTION_META.update;
                    const Icon = em.icon;
                    return (
                      <div key={r.id} className="flex gap-3 px-4 py-3 transition-colors duration-150 hover:bg-[hsl(var(--surface-2))]" data-testid="audit-entry">
                        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg" style={{ background: `${em.color}1f`, color: em.color }}>
                          <Icon className="h-4 w-4" />
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase", am.chip)}>
                              <am.icon className="h-2.5 w-2.5" />{am.label}
                            </span>
                            <span className="text-sm font-medium">{r.entity_type}</span>
                            {r.label && <span className="truncate text-sm text-muted-foreground">· {r.label}</span>}
                            <span className="ml-auto text-[11px] tabular-nums text-muted-foreground" title={r.changed_at}>{timeOf(r.changed_at)}</span>
                          </div>
                          <p className="mt-0.5 text-xs text-muted-foreground">by <span className="font-medium text-foreground">{r.changed_by}</span></p>
                          {r.reason && (
                            <p className="mt-1.5 flex items-start gap-1.5 rounded-md border-l-2 border-[hsl(var(--info))] bg-[hsl(var(--info)/0.06)] px-2 py-1 text-[11px] text-foreground" data-testid="audit-entry-reason">
                              <MessageSquare className="mt-0.5 h-3 w-3 shrink-0 text-[hsl(var(--info))]" />
                              <span><span className="text-muted-foreground">Reason:</span> {r.reason}</span>
                            </p>
                          )}
                          {r.changes && r.changes.length > 0 && (
                            <div className="mt-2 overflow-hidden rounded-lg border" data-testid="audit-entry-diff">
                              <table className="w-full text-xs">
                                <thead className="bg-[hsl(var(--surface-2))] text-[10px] uppercase tracking-wide text-muted-foreground">
                                  <tr>
                                    <th className="px-2.5 py-1.5 text-left font-medium">Field</th>
                                    <th className="px-2.5 py-1.5 text-left font-medium">Before</th>
                                    <th className="w-6 px-1 py-1.5" />
                                    <th className="px-2.5 py-1.5 text-left font-medium">After</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y">
                                  {r.changes.map((c, i) => (
                                    <tr key={i} data-testid="audit-diff-row">
                                      <td className="px-2.5 py-1.5 font-medium text-muted-foreground">{c.field}</td>
                                      <td className="px-2.5 py-1.5">
                                        <span className="rounded bg-[hsl(var(--danger)/0.08)] px-1.5 py-0.5 tabular-nums text-[hsl(var(--danger))] line-through decoration-[hsl(var(--danger)/0.5)]" data-testid="audit-diff-before">{fmtVal(c.old, money)}</span>
                                      </td>
                                      <td className="px-1 py-1.5 text-center"><ArrowRight className="mx-auto h-3 w-3 text-muted-foreground" /></td>
                                      <td className="px-2.5 py-1.5">
                                        <span className="rounded bg-[hsl(var(--success)/0.1)] px-1.5 py-0.5 font-medium tabular-nums text-[hsl(var(--success))]" data-testid="audit-diff-after">{fmtVal(c.new, money)}</span>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>
    </div>
  );
}
